import React, { useState, useEffect } from 'react';
import html2pdf from 'html2pdf.js';
import logo from '../../assets/logo.png';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000';

const themeGray = '#f1f1f1';
const themeDark = '#6e6e6e';
const themeGreen = '#90b41c';
const themeBlue = '#cce5ff';

const ImagePlaceholderSVG = () => (
    <svg viewBox="0 0 200 200" preserveAspectRatio="none" style={{ width: '100%', height: '100%', display: 'block', backgroundColor: '#d6f0ff' }}>
        {/* Cloud */}
        <circle cx="80" cy="75" r="20" fill="white" />
        <circle cx="105" cy="60" r="25" fill="white" />
        <circle cx="130" cy="75" r="20" fill="white" />
        <rect x="80" y="75" width="50" height="20" fill="white" />
        {/* Back Hill */}
        <path d="M 0 160 Q 50 130 120 160 T 200 150 L 200 200 L 0 200 Z" fill="#b8d465" />
        {/* Front Hill */}
        <path d="M -20 200 Q 50 150 130 190 T 220 170 L 220 200 Z" fill="#88af00" />
    </svg>
);

const RegistroFotografico = ({ patient, onCancel }) => {
    const [images, setImages] = useState([]);
    const [loading, setLoading] = useState(false);
    const [activeTab, setActiveTab] = useState('extraoral');
    
    // Custom Notes Modal state
    const [pendingNoteUpload, setPendingNoteUpload] = useState(null);
    const [noteText, setNoteText] = useState('');

    useEffect(() => {
        loadImages();
    }, [patient.id]);

    const loadImages = async () => {
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/clinical-records/patient-images/?patient=${patient.id}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                const data = await res.json();
                setImages(data.results || data);
            }
        } catch (error) {
            console.error('Error fetching images:', error);
        }
    };

    const handleUpload = async (category, subcategory, file, notes = '') => {
        setLoading(true);
        const token = localStorage.getItem('token');
        const formData = new FormData();
        formData.append('patient', patient.id);
        formData.append('category', category);
        if (subcategory) formData.append('subcategory', subcategory);
        formData.append('image', file);
        if (notes) formData.append('notes', notes);

        try {
            const res = await fetch(`${API_URL}/api/clinical-records/patient-images/`, {
                method: 'POST',
                headers: { 'Authorization': `Bearer ${token}` },
                body: formData
            });
            if (res.ok) {
                loadImages();
            } else {
                alert('Error al subir la imagen');
            }
        } catch (error) {
            console.error('Error uploading:', error);
            alert('Error al subir la imagen');
        }
        setLoading(false);
    };

    const handleDelete = async (id) => {
        if (!window.confirm('¿Seguro que deseas eliminar esta imagen?')) return;
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const res = await fetch(`${API_URL}/api/clinical-records/patient-images/${id}/`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (res.ok) {
                loadImages();
            }
        } catch (error) {
            console.error('Error deleting:', error);
        }
        setLoading(false);
    };

    const handleExportPDF = () => {
        const wrapper = document.getElementById('pdf-report-container-wrapper');
        const element = document.getElementById('pdf-report-container');
        if (!element || !wrapper) return;
        
        // Show the wrapper but push it off-screen so html2canvas can capture it
        wrapper.style.display = 'block';
        wrapper.style.position = 'absolute';
        wrapper.style.left = '-9999px';
        wrapper.style.top = '0';
        
        const opt = {
          margin:       10,
          filename:     `Registro_Fotografico_${patient.first_name}_${patient.last_name}.pdf`,
          image:        { type: 'jpeg', quality: 0.98 },
          html2canvas:  { scale: 2, useCORS: true },
          jsPDF:        { unit: 'mm', format: 'a4', orientation: 'portrait' }
        };

        html2pdf().set(opt).from(element).save().then(() => {
            wrapper.style.display = 'none';
        });
    };

    const getImage = (category, subcategory = null) => {
        // Return the first match. For multiple (like periapical), we handle differently
        if (subcategory) {
            return images.find(img => img.category === category && img.subcategory === subcategory);
        }
        return images.find(img => img.category === category);
    };

    const getImages = (category) => {
        return images.filter(img => img.category === category);
    };

    const handleFileChange = (e, category, subcategory = null, notes = '') => {
        const file = e.target.files[0];
        if (file) {
            handleUpload(category, subcategory, file, notes);
        }
    };

    const ImageBox = ({ category, subcategory, label, width = '30%' }) => {
        const img = getImage(category, subcategory);
        const inputId = `upload-${category}-${subcategory}`;

        return (
            <div style={{ width: width, textAlign: 'center', marginBottom: '20px' }}>
                <div 
                    style={{ 
                        backgroundColor: '#d6f0ff', 
                        height: '180px', 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        overflow: 'hidden',
                        position: 'relative',
                        cursor: 'pointer',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                    }}
                    onClick={() => !img && document.getElementById(inputId).click()}
                >
                    {img ? (
                        <>
                            <img src={img.image} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                            <button 
                                onClick={(e) => { e.stopPropagation(); handleDelete(img.id); }}
                                className="btn btn-sm btn-danger"
                                style={{ position: 'absolute', top: 5, right: 5, padding: '2px 6px', borderRadius: '50%' }}
                            >
                                <i className="bi bi-x"></i>
                            </button>
                        </>
                    ) : (
                        <ImagePlaceholderSVG />
                    )}
                </div>
                <input 
                    type="file" 
                    id={inputId} 
                    style={{ display: 'none' }} 
                    accept="image/*"
                    onChange={(e) => handleFileChange(e, category, subcategory)}
                />
                <h6 style={{ marginTop: '10px', color: '#6b6b6b', fontWeight: 'bold', fontSize: '14px' }}>{label}</h6>
            </div>
        );
    };

    const MultipleImageBox = ({ category, label, showNotes = false }) => {
        const imgs = getImages(category);
        const inputId = `upload-multiple-${category}`;

        const isPdf = (url) => url && url.toLowerCase().endsWith('.pdf');
        const isWord = (url) => url && (url.toLowerCase().endsWith('.doc') || url.toLowerCase().endsWith('.docx'));

        return (
            <div style={{ marginBottom: '30px' }}>
                <h5 style={{ color: '#6b6b6b', fontWeight: 'bold' }}>{label}</h5>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginTop: '15px' }}>
                    {imgs.map(img => (
                        <div key={img.id} style={{ width: '30%', minWidth: '150px', textAlign: 'center', marginBottom: '15px' }}>
                            <div style={{ position: 'relative', backgroundColor: '#e5e7eb', height: '150px', overflow: 'hidden', boxShadow: '0 2px 5px rgba(0,0,0,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                {isPdf(img.image) ? (
                                    <a href={img.image} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#dc3545' }}>
                                        <i className="bi bi-file-earmark-pdf-fill" style={{ fontSize: '4rem' }}></i>
                                        <div style={{ fontSize: '12px', marginTop: '10px' }}>Ver PDF</div>
                                    </a>
                                ) : isWord(img.image) ? (
                                    <a href={img.image} target="_blank" rel="noopener noreferrer" style={{ textDecoration: 'none', color: '#0d6efd' }}>
                                        <i className="bi bi-file-earmark-word-fill" style={{ fontSize: '4rem' }}></i>
                                        <div style={{ fontSize: '12px', marginTop: '10px' }}>Ver Documento</div>
                                    </a>
                                ) : (
                                    <img src={img.image} alt="Documento" style={{ width: '100%', height: '100%', objectFit: 'cover' }} onClick={() => window.open(img.image, '_blank')} />
                                )}
                                <button 
                                    onClick={() => handleDelete(img.id)}
                                    className="btn btn-sm btn-danger"
                                    style={{ position: 'absolute', top: 5, right: 5, padding: '2px 6px', borderRadius: '50%' }}
                                >
                                    <i className="bi bi-x"></i>
                                </button>
                            </div>
                            {showNotes && (
                                <p style={{ fontSize: '12px', color: '#6b6b6b', marginTop: '5px', wordWrap: 'break-word', fontWeight: 'bold' }}>
                                    {img.notes || 'Sin descripción'}
                                </p>
                            )}
                        </div>
                    ))}

                    <div style={{ width: '30%', minWidth: '150px', textAlign: 'center', marginBottom: '15px' }}>
                        <div 
                            style={{ 
                                backgroundColor: '#d6f0ff', 
                                height: '150px', 
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'pointer',
                                flexDirection: 'column',
                                boxShadow: '0 2px 5px rgba(0,0,0,0.1)'
                            }}
                            onClick={() => document.getElementById(inputId).click()}
                        >
                            <i className="bi bi-plus-circle" style={{ fontSize: '2.5rem', color: '#6b6b6b' }}></i>
                            <span style={{ fontSize: '12px', color: '#6b6b6b', marginTop: '10px', fontWeight: 'bold' }}>Añadir más...</span>
                        </div>
                        <input 
                            type="file" 
                            id={inputId} 
                            style={{ display: 'none' }} 
                            accept={['consentimiento', 'receta', 'laboratorio'].includes(category) ? "image/*,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document" : "image/*"}
                            onChange={(e) => {
                                const file = e.target.files[0];
                                if (!file) return;
                                if (showNotes) {
                                    setPendingNoteUpload({ file, category, inputTarget: e.target });
                                    setNoteText('');
                                } else {
                                    handleUpload(category, null, file, '');
                                    e.target.value = null;
                                }
                            }}
                        />
                    </div>
                </div>
            </div>
        );
    };

    const PrintImageBox = ({ category, subcategory, label, width = '30%' }) => {
        const img = getImage(category, subcategory);
        return (
            <div style={{ width: width, textAlign: 'center', marginBottom: '20px' }}>
                <div style={{ backgroundColor: '#d6f0ff', height: '180px', display: 'flex', alignItems: 'center', justifyContent: 'center', overflow: 'hidden', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}>
                    {img ? (
                        <img src={img.image} alt={label} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                    ) : (
                        <ImagePlaceholderSVG />
                    )}
                </div>
                <h6 style={{ marginTop: '10px', color: '#6b6b6b', fontWeight: 'bold', fontSize: '14px' }}>{label}</h6>
            </div>
        );
    };

    const PrintMultipleImageBox = ({ category, label, showNotes = false, large = false }) => {
        const imgs = getImages(category);
        if (imgs.length === 0) return null;
        
        const boxWidth = large ? '48%' : '30%';
        const boxHeight = large ? '300px' : '180px';

        return (
            <div style={{ marginBottom: '30px' }}>
                <h5 style={{ color: '#6b6b6b', fontWeight: 'bold' }}>{label}</h5>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '15px', marginTop: '15px' }}>
                    {imgs.map(img => (
                        <div key={img.id} style={{ width: boxWidth, minWidth: '150px', textAlign: 'center', marginBottom: '15px' }}>
                            <div style={{ backgroundColor: '#e5e7eb', height: boxHeight, overflow: 'hidden', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}>
                                <img src={img.image} alt="Radiografía" style={{ width: '100%', height: '100%', objectFit: 'contain' }} />
                            </div>
                            {showNotes && (
                                <p style={{ fontSize: '14px', color: '#6b6b6b', marginTop: '10px', wordWrap: 'break-word', fontWeight: 'bold' }}>
                                    {img.notes || 'Sin descripción'}
                                </p>
                            )}
                        </div>
                    ))}
                </div>
            </div>
        );
    };

    return (
        <div style={{ position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh', backgroundColor: 'rgba(0,0,0,0.6)', zIndex: 9999, display: 'flex', justifyContent: 'center', alignItems: 'center', padding: '20px' }}>
            <div style={{ width: '100%', maxWidth: '1140px', maxHeight: '90vh', backgroundColor: themeGray, borderRadius: '15px', display: 'flex', flexDirection: 'column', boxShadow: '0 10px 30px rgba(0,0,0,0.5)', overflow: 'hidden', position: 'relative' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '15px 20px', backgroundColor: themeGray }}>
                    <button onClick={handleExportPDF} className="btn btn-primary" style={{ backgroundColor: '#90b41c', border: 'none', fontWeight: 'bold', borderRadius: '8px' }}>
                        <i className="bi bi-file-earmark-pdf-fill me-2"></i> Exportar / Imprimir Reporte
                    </button>
                    <button type="button" onClick={onCancel} style={{ background: 'transparent', border: 'none', fontSize: '1.5rem', cursor: 'pointer', color: themeDark }}>
                        <i className="bi bi-x-lg"></i>
                    </button>
                </div>
                <div style={{ padding: '0 40px 40px 40px', overflowY: 'auto', flex: 1 }}>
                        
                        {/* REGISTRO FOTOGRÁFICO */}
                        <div className="mb-5">
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '1.5rem' }}>
                                <img src={logo} alt="Lemnardental Logo" style={{ width: '110px', height: '110px', borderRadius: '50%', objectFit: 'contain', marginRight: '20px', border: '3px solid #9ca3af', padding: '5px' }} />
                                <div>
                                    <h1 style={{ color: '#6b6b6b', fontWeight: '900', margin: 0, letterSpacing: '1px' }}>
                                        {activeTab === 'radiologico' ? 'REGISTRO RADIOLÓGICO' : (activeTab === 'consentimiento' ? 'CONSENTIMIENTO INFORMADO' : (activeTab === 'receta' ? 'RECETAS' : 'REGISTRO FOTOGRÁFICO'))}
                                    </h1>
                                    <h5 style={{ color: '#6b6b6b', margin: 0 }}>Paciente: <strong>{patient.first_name} {patient.last_name}</strong></h5>
                                </div>
                            </div>

                            <div style={{ display: 'flex', borderBottom: `2px solid ${themeDark}`, marginBottom: '30px' }}>
                                <button 
                                    style={{ 
                                        padding: '10px 20px', 
                                        color: activeTab === 'extraoral' ? themeDark : '#9ca3af', 
                                        fontWeight: activeTab === 'extraoral' ? 'bold' : 'normal', 
                                        backgroundColor: activeTab === 'extraoral' ? 'white' : 'transparent', 
                                        border: activeTab === 'extraoral' ? `2px solid ${themeDark}` : 'none', 
                                        borderBottom: 'none',
                                        borderRadius: '8px 8px 0 0',
                                        cursor: 'pointer',
                                        marginBottom: '-2px' // Overlap the bottom border
                                    }}
                                    onClick={() => setActiveTab('extraoral')}
                                >
                                    Fotografía Extraoral
                                </button>
                                <button 
                                    style={{ 
                                        padding: '10px 20px', 
                                        color: activeTab === 'intraoral' ? themeDark : '#9ca3af', 
                                        fontWeight: activeTab === 'intraoral' ? 'bold' : 'normal', 
                                        backgroundColor: activeTab === 'intraoral' ? 'white' : 'transparent', 
                                        border: activeTab === 'intraoral' ? `2px solid ${themeDark}` : 'none', 
                                        borderBottom: 'none',
                                        borderRadius: '8px 8px 0 0',
                                        cursor: 'pointer',
                                        marginBottom: '-2px'
                                    }}
                                    onClick={() => setActiveTab('intraoral')}
                                >
                                    Fotografía Intraoral y Arcos
                                </button>
                                <button 
                                    style={{ 
                                        padding: '10px 20px', 
                                        color: activeTab === 'radiologico' ? themeDark : '#9ca3af', 
                                        fontWeight: activeTab === 'radiologico' ? 'bold' : 'normal', 
                                        backgroundColor: activeTab === 'radiologico' ? 'white' : 'transparent', 
                                        border: activeTab === 'radiologico' ? `2px solid ${themeDark}` : 'none', 
                                        borderBottom: 'none',
                                        borderRadius: '8px 8px 0 0',
                                        cursor: 'pointer',
                                        marginBottom: '-2px'
                                    }}
                                    onClick={() => setActiveTab('radiologico')}
                                >
                                    Registro Radiológico
                                </button>
                                <button 
                                    style={{ 
                                        padding: '10px 20px', 
                                        color: activeTab === 'consentimiento' ? themeDark : '#9ca3af', 
                                        fontWeight: activeTab === 'consentimiento' ? 'bold' : 'normal', 
                                        backgroundColor: activeTab === 'consentimiento' ? 'white' : 'transparent', 
                                        border: activeTab === 'consentimiento' ? `2px solid ${themeDark}` : 'none', 
                                        borderBottom: 'none',
                                        borderRadius: '8px 8px 0 0',
                                        cursor: 'pointer',
                                        marginBottom: '-2px'
                                    }}
                                    onClick={() => setActiveTab('consentimiento')}
                                >
                                    Consentimiento Informado
                                </button>
                                <button 
                                    style={{ 
                                        padding: '10px 20px', 
                                        color: activeTab === 'receta' ? themeDark : '#9ca3af', 
                                        fontWeight: activeTab === 'receta' ? 'bold' : 'normal', 
                                        backgroundColor: activeTab === 'receta' ? 'white' : 'transparent', 
                                        border: activeTab === 'receta' ? `2px solid ${themeDark}` : 'none', 
                                        borderBottom: 'none',
                                        borderRadius: '8px 8px 0 0',
                                        cursor: 'pointer',
                                        marginBottom: '-2px'
                                    }}
                                    onClick={() => setActiveTab('receta')}
                                >
                                    Receta
                                </button>
                                <button 
                                    style={{ 
                                        padding: '10px 20px', 
                                        color: activeTab === 'laboratorio' ? themeDark : '#9ca3af', 
                                        fontWeight: activeTab === 'laboratorio' ? 'bold' : 'normal', 
                                        backgroundColor: activeTab === 'laboratorio' ? 'white' : 'transparent', 
                                        border: activeTab === 'laboratorio' ? `2px solid ${themeDark}` : 'none', 
                                        borderBottom: 'none',
                                        borderRadius: '8px 8px 0 0',
                                        cursor: 'pointer',
                                        marginBottom: '-2px'
                                    }}
                                    onClick={() => setActiveTab('laboratorio')}
                                >
                                    Exámenes de laboratorio
                                </button>
                            </div>

                            {activeTab === 'extraoral' && (
                                <>

                            <h5 style={{ color: '#6b6b6b', fontWeight: 'bold' }}>FOTOGRAFÍA EXTRAORAL ( Perfil en reposo )</h5>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginBottom: '1.5rem' }}>
                                <ImageBox category="extraoral_reposo" subcategory="lateral_derecha" label="Lateral derecha" />
                                <ImageBox category="extraoral_reposo" subcategory="frente" label="Frente" />
                                <ImageBox category="extraoral_reposo" subcategory="lateral_izquierda" label="Lateral izquierda" />
                            </div>

                            <h5 style={{ color: '#6b6b6b', fontWeight: 'bold' }}>FOTOGRAFÍA EXTRAORAL (Perfil con sonrisa)</h5>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginBottom: '1.5rem' }}>
                                <ImageBox category="extraoral_sonrisa" subcategory="lateral_derecha" label="Lateral derecha" />
                                <ImageBox category="extraoral_sonrisa" subcategory="frente" label="Frente" />
                                <ImageBox category="extraoral_sonrisa" subcategory="lateral_izquierda" label="Lateral izquierda" />
                            </div>

                                </>
                            )}

                            {activeTab === 'intraoral' && (
                                <>
                                    <h5 style={{ color: '#6b6b6b', fontWeight: 'bold' }}>FOTOGRAFÍA INTRAORAL</h5>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginBottom: '1.5rem' }}>
                                        <ImageBox category="intraoral" subcategory="lateral_derecha" label="Lateral derecha" />
                                        <ImageBox category="intraoral" subcategory="frente" label="Frente" />
                                        <ImageBox category="intraoral" subcategory="lateral_izquierda" label="Lateral izquierda" />
                                    </div>

                                    <h5 style={{ color: '#6b6b6b', fontWeight: 'bold' }}>ARCOS DENTALES</h5>
                                    <div style={{ display: 'flex', justifyContent: 'space-around', gap: '20px', marginBottom: '1.5rem' }}>
                                        <ImageBox category="arcos_dentales" subcategory="superior" label="Superior" width="45%" />
                                        <ImageBox category="arcos_dentales" subcategory="inferior" label="Inferior" width="45%" />
                                    </div>
                                </>
                            )}

                            {activeTab === 'radiologico' && (
                                <>
                                    <MultipleImageBox category="radiografia_panoramica" label="RADIOGRAFÍA PANORÁMICA" showNotes={true} />

                                    <MultipleImageBox category="radiografia_periapical" label="RADIOGRAFÍAS PERIAPICALES" showNotes={true} />
                                </>
                            )}

                            {activeTab === 'consentimiento' && (
                                <>
                                    <MultipleImageBox category="consentimiento" label="HOJAS ESCANEADAS DE CONSENTIMIENTO INFORMADO" showNotes={true} />
                                </>
                            )}

                            {activeTab === 'receta' && (
                                <>
                                    <MultipleImageBox category="receta" label="FOTOS O SCANS DE RECETAS" showNotes={true} />
                                </>
                            )}

                            {activeTab === 'laboratorio' && (
                                <>
                                    <MultipleImageBox category="laboratorio" label="EXÁMENES DE LABORATORIO" showNotes={true} />
                                </>
                            )}
                        </div>

                    </div>
                    
                    {/* HIDDEN PDF CONTAINER */}
                    <div style={{ display: 'none' }} id="pdf-report-container-wrapper">
                        <div id="pdf-report-container" style={{ padding: '30px', backgroundColor: '#f1f1f1', color: '#6b6b6b', width: '720px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', marginBottom: '40px' }}>
                                <img src={logo} alt="Lemnardental Logo" style={{ width: '150px', height: '150px', borderRadius: '50%', objectFit: 'contain', marginRight: '30px', border: '4px solid #9ca3af', padding: '5px' }} />
                                <div>
                                    <h1 style={{ color: '#6b6b6b', fontWeight: '900', fontSize: '36px', margin: 0, letterSpacing: '1px' }}>REPORTE FOTOGRÁFICO</h1>
                                    <h3 style={{ color: '#6b6b6b', margin: '10px 0 0 0' }}>Paciente: <strong>{patient.first_name} {patient.last_name}</strong></h3>
                                </div>
                            </div>

                            <h4 style={{ color: '#6b6b6b', fontWeight: 'bold' }}>FOTOGRAFÍA EXTRAORAL ( Perfil en reposo )</h4>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginBottom: '30px' }}>
                                <PrintImageBox category="extraoral_reposo" subcategory="lateral_derecha" label="Lateral derecha" />
                                <PrintImageBox category="extraoral_reposo" subcategory="frente" label="Frente" />
                                <PrintImageBox category="extraoral_reposo" subcategory="lateral_izquierda" label="Lateral izquierda" />
                            </div>

                            <h4 style={{ color: '#6b6b6b', fontWeight: 'bold' }}>FOTOGRAFÍA EXTRAORAL (Perfil con sonrisa)</h4>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginBottom: '30px' }}>
                                <PrintImageBox category="extraoral_sonrisa" subcategory="lateral_derecha" label="Lateral derecha" />
                                <PrintImageBox category="extraoral_sonrisa" subcategory="frente" label="Frente" />
                                <PrintImageBox category="extraoral_sonrisa" subcategory="lateral_izquierda" label="Lateral izquierda" />
                            </div>

                            <h4 style={{ color: '#6b6b6b', fontWeight: 'bold' }}>FOTOGRAFÍA INTRAORAL</h4>
                            <div style={{ display: 'flex', justifyContent: 'space-between', gap: '20px', marginBottom: '30px' }}>
                                <PrintImageBox category="intraoral" subcategory="lateral_derecha" label="Lateral derecha" />
                                <PrintImageBox category="intraoral" subcategory="frente" label="Frente" />
                                <PrintImageBox category="intraoral" subcategory="lateral_izquierda" label="Lateral izquierda" />
                            </div>

                            <div className="html2pdf__page-break" style={{ pageBreakBefore: 'always' }}></div>
                            <h4 style={{ color: '#6b6b6b', fontWeight: 'bold' }}>ARCOS DENTALES</h4>
                            <div style={{ display: 'flex', justifyContent: 'space-around', gap: '20px', marginBottom: '30px' }}>
                                <PrintImageBox category="arcos_dentales" subcategory="superior" label="Superior" width="45%" />
                                <PrintImageBox category="arcos_dentales" subcategory="inferior" label="Inferior" width="45%" />
                            </div>

                            <PrintMultipleImageBox category="radiografia_panoramica" label="RADIOGRAFÍA PANORÁMICA" showNotes={true} />
                            <PrintMultipleImageBox category="radiografia_periapical" label="RADIOGRAFÍAS PERIAPICALES" showNotes={true} />
                            <PrintMultipleImageBox category="laboratorio" label="EXÁMENES DE LABORATORIO" showNotes={true} />
                        </div>
                    </div>
                    
                    {loading && (
                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(255,255,255,0.7)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '15px' }}>
                            <div className="spinner-border text-primary" role="status" style={{ width: '3rem', height: '3rem' }}>
                                <span className="visually-hidden">Loading...</span>
                            </div>
                        </div>
                    )}

                    {/* CUSTOM NOTES MODAL */}
                    {pendingNoteUpload && (
                        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.7)', zIndex: 10000, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: '15px' }}>
                            <div style={{ width: '400px', backgroundColor: 'white', padding: '30px', borderRadius: '15px', boxShadow: '0 5px 15px rgba(0,0,0,0.3)', textAlign: 'center' }}>
                                <h4 style={{ color: '#6b6b6b', fontWeight: 'bold', marginBottom: '20px' }}>Añadir Descripción</h4>
                                <p style={{ color: '#6b6b6b', fontSize: '14px', marginBottom: '20px' }}>Introduce un texto para la parte inferior de esta radiografía (opcional):</p>
                                <textarea
                                    className="form-control"
                                    rows="3"
                                    value={noteText}
                                    onChange={(e) => setNoteText(e.target.value)}
                                    placeholder="Ej: Radiografía panorámica inicial..."
                                    style={{ marginBottom: '20px', resize: 'none' }}
                                />
                                <div style={{ display: 'flex', justifyContent: 'space-between', gap: '15px' }}>
                                    <button 
                                        className="btn btn-secondary flex-grow-1"
                                        onClick={() => {
                                            if(pendingNoteUpload.inputTarget) pendingNoteUpload.inputTarget.value = null;
                                            setPendingNoteUpload(null);
                                        }}
                                        style={{ fontWeight: 'bold', borderRadius: '8px' }}
                                    >
                                        Cancelar
                                    </button>
                                    <button 
                                        className="btn btn-primary flex-grow-1"
                                        onClick={() => {
                                            handleUpload(pendingNoteUpload.category, null, pendingNoteUpload.file, noteText);
                                            if(pendingNoteUpload.inputTarget) pendingNoteUpload.inputTarget.value = null;
                                            setPendingNoteUpload(null);
                                        }}
                                        style={{ backgroundColor: themeGreen, border: 'none', fontWeight: 'bold', borderRadius: '8px' }}
                                    >
                                        Guardar Imagen
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
        </div>
    );
};

export default RegistroFotografico;
